import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MailX, MousePointerClick, Link2, PlayCircle, CheckCircle2, Send, Eye, ChevronDown, ChevronUp, Search, RefreshCw, Megaphone } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AnnouncementCard = () => {
  const { t } = useAdminT();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [showList, setShowList] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/announcement/preview`, { headers: authHeaders(), timeout: 60000 });
      setPreview(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!preview) return;
    if (!window.confirm(t(`سيتم إرسال الإعلان إلى ${preview.pending} عميلة الآن — متابعة؟`))) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/announcement/send`, {}, { headers: authHeaders(), timeout: 60000 });
      if (res.data.done) {
        toast.success(t("الإعلان مُرسل مسبقاً للجميع — لا يوجد مستحقات جديدة"));
      } else {
        toast.success(t(`انطلق الإرسال إلى ${res.data.queued} عميلة — يتم بوتيرة آمنة في الخلفية (حوالي ${Math.ceil(res.data.queued * 8 / 60)} دقيقة)`));
      }
      setTimeout(loadPreview, 5000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="announcement-card" className="border border-luxury-gold/40 bg-luxury-gold/5 p-5 md:p-6 mb-10">
      <div className="flex items-center gap-3 mb-2">
        <Megaphone className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
        <h2 className="font-serif text-xl text-luxury-bone">{t("الإعلان الأخير للعميلات القديمات")}</h2>
      </div>
      <p className="text-luxury-gray text-sm leading-relaxed mb-4">
        {t("يرسل إيميلاً واحداً لعميلات كاجابي اللواتي لم يضغطن أي رابط سحري بعد — يشرح الانتقال للمنصة الجديدة وطريقتي الدخول. كل عميلة تستلمه مرة واحدة فقط، والإرسال متدرّج حتى لا تتعثر الرسائل.")}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="announcement-preview-button" onClick={loadPreview} disabled={busy}
          className="flex items-center gap-2 border border-luxury-gold text-luxury-gold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-gold hover:text-luxury-black transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
          {t("معاينة المستحقات والنص")}
        </button>
        {preview && preview.pending > 0 && (
          <button data-testid="announcement-send-button" onClick={send} disabled={sending}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
            {t(`إرسال الإعلان إلى ${preview.pending} عميلة`)}
          </button>
        )}
      </div>
      {preview && (
        <div data-testid="announcement-result" className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-luxury-border bg-luxury-surface p-3 text-center">
              <p className="font-serif text-2xl text-luxury-bone" data-testid="announcement-total">{preview.total_legacy}</p>
              <p className="text-luxury-gray text-xs">{t("عميلات قديمات بلا نقرة")}</p>
            </div>
            <div className="border border-luxury-border bg-luxury-surface p-3 text-center">
              <p className="font-serif text-2xl text-green-400" data-testid="announcement-sent-count">{preview.already_sent}</p>
              <p className="text-luxury-gray text-xs">{t("استلمناه مسبقاً")}</p>
            </div>
            <div className="border border-luxury-border bg-luxury-surface p-3 text-center">
              <p className="font-serif text-2xl text-luxury-gold" data-testid="announcement-pending-count">{preview.pending}</p>
              <p className="text-luxury-gray text-xs">{t("بانتظار الإرسال")}</p>
            </div>
          </div>
          <p className="text-luxury-gray text-xs" dir="rtl">{t("الموضوع:")} <span className="text-luxury-bone">{preview.subject}</span></p>
          <div className="border border-luxury-border overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: preview.html }} data-testid="announcement-html-preview" />
          </div>
          {preview.recipients.length > 0 && (
            <div>
              <button data-testid="announcement-recipients-toggle" onClick={() => setShowList((s) => !s)}
                className="flex items-center gap-2 text-luxury-gray hover:text-luxury-gold text-xs transition-colors">
                {showList ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {t("قائمة المستحقات")} ({preview.recipients.length})
              </button>
              {showList && (
                <div data-testid="announcement-recipients" dir="ltr" className="mt-2 max-h-48 overflow-y-auto border border-luxury-border bg-luxury-black p-3 text-xs text-luxury-gray space-y-1">
                  {preview.recipients.map((e) => <p key={e}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProgressBar = ({ pct }) => (
  <div className="h-1.5 w-full bg-luxury-black rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${pct >= 90 ? "bg-green-400" : "bg-luxury-gold"}`} style={{ width: `${Math.min(100, pct)}%` }} />
  </div>
);

const ClientRow = ({ row, index, t }) => {
  const [open, setOpen] = useState(false);
  const activeCourses = row.courses.filter((c) => c.active);
  return (
    <div className="border border-luxury-border bg-luxury-surface" data-testid={`activity-row-${index}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full p-4 flex items-center gap-4 text-start flex-wrap" data-testid={`activity-row-toggle-${index}`}>
        <div className="flex-1 min-w-[200px]">
          <p className="text-luxury-bone text-sm font-semibold" dir="ltr">{row.email}</p>
          {row.name && <p className="text-luxury-gray text-xs mt-0.5">{row.name}</p>}
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <span className="flex items-center gap-1.5 text-luxury-gray" title={t("الدورات الفعالة")}>
            <PlayCircle className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} /> {activeCourses.length}
          </span>
          <span className="flex items-center gap-1.5 text-luxury-gray" title={t("إيميلات ناجحة / فاشلة")}>
            {row.emails_failed > 0 ? <MailX className="w-4 h-4 text-red-400" strokeWidth={1.5} /> : <Mail className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />}
            {row.emails_sent}{row.emails_failed > 0 ? ` / ${row.emails_failed}✗` : ""}
          </span>
          <span className={`flex items-center gap-1.5 ${row.links_clicked > 0 ? "text-green-400" : "text-luxury-gray"}`} title={t("روابط ضُغطت / أُرسلت")}>
            <MousePointerClick className="w-4 h-4" strokeWidth={1.5} /> {row.links_clicked}/{row.links_total}
          </span>
          <span className="flex items-center gap-1.5 text-luxury-gray" title={t("فيديوهات شاهدتها / أكملتها")}>
            <CheckCircle2 className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} /> {row.watched_count}{row.completed_count > 0 ? ` (${row.completed_count}✓)` : ""}
          </span>
          <span className="text-luxury-gray/70 hidden md:inline" dir="ltr">{fmtDate(row.last_activity_at)}</span>
          {open ? <ChevronUp className="w-4 h-4 text-luxury-gold" /> : <ChevronDown className="w-4 h-4 text-luxury-gray" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-luxury-border p-4 grid md:grid-cols-2 gap-6" data-testid={`activity-detail-${index}`}>
          <div>
            <p className="text-luxury-gold text-xs font-semibold mb-3">{t("الدورات")}</p>
            {row.courses.length === 0 && <p className="text-luxury-gray text-xs">{t("لا توجد صلاحيات")}</p>}
            <div className="space-y-2">
              {row.courses.map((c) => (
                <div key={c.course_id} className="flex items-center justify-between gap-3 text-xs border border-luxury-border/60 p-2.5">
                  <span className="text-luxury-bone">{c.title}</span>
                  <span className={c.active ? "text-green-400" : "text-red-400"}>
                    {c.revoked ? t("ملغاة") : c.active ? (c.expires_at ? `${t("حتى")} ${fmtDate(c.expires_at)}` : t("مدى الحياة")) : t("منتهية")}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-luxury-gold text-xs font-semibold mt-5 mb-3">{t("آخر الإيميلات")}</p>
            {row.emails.length === 0 && <p className="text-luxury-gray text-xs">{t("لم ترسل إيميلات")}</p>}
            <div className="space-y-2">
              {row.emails.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {e.ok ? <Mail className="w-3.5 h-3.5 text-green-400 shrink-0" /> : <MailX className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                  <span className="text-luxury-bone flex-1 truncate">{e.subject}</span>
                  <span className="text-luxury-gray/70 shrink-0" dir="ltr">{fmtDate(e.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-luxury-gold text-xs font-semibold mb-3">{t("تقدم المشاهدة")}</p>
            {row.watch.length === 0 && <p className="text-luxury-gray text-xs">{t("لم تشاهد أي فيديو بعد")}</p>}
            <div className="space-y-3">
              {row.watch.map((w) => (
                <div key={w.video_id} className="text-xs">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-luxury-bone truncate">{w.title}</span>
                    <span className={w.completed ? "text-green-400 shrink-0" : "text-luxury-gold shrink-0"} dir="ltr">{w.pct}%</span>
                  </div>
                  <ProgressBar pct={w.pct} />
                  <p className="text-luxury-gray/60 mt-1" dir="ltr">{fmtDate(w.last_watched_at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-luxury-gray space-y-1.5 border-t border-luxury-border/60 pt-3">
              <p className="flex items-center gap-2"><Link2 className="w-3.5 h-3.5 text-luxury-gold" /> {t("روابط مولّدة")}: {row.links_total} — {t("ضُغطت")}: {row.links_clicked}</p>
              <p>{t("آخر نقرة رابط")}: <span dir="ltr">{fmtDate(row.last_link_click_at)}</span></p>
              <p>{t("آخر تسجيل دخول")}: <span dir="ltr">{fmtDate(row.last_login_at)}</span></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ActivityTab = () => {
  const { t } = useAdminT();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    axios.get(`${API}/admin/activity`, { headers: authHeaders(), timeout: 60000 })
      .then((res) => setRows(res.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <div data-testid="activity-tab">
      <AnnouncementCard />
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h2 className="font-serif text-xl text-luxury-bone">{t("نشاط العميلات")}</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-luxury-gray absolute top-1/2 -translate-y-1/2 start-3" strokeWidth={1.5} />
            <input
              data-testid="activity-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("ابحثي بالبريد أو الاسم...")}
              className="bg-luxury-black border border-luxury-border text-luxury-bone text-sm ps-9 pe-4 py-2.5 rounded-full focus:outline-none focus:border-luxury-gold w-64"
            />
          </div>
          <button data-testid="activity-refresh-button" onClick={load} className="border border-luxury-border text-luxury-gray hover:text-luxury-gold hover:border-luxury-gold p-2.5 rounded-full transition-colors">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {!rows ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-luxury-gray text-sm text-center py-16" data-testid="activity-empty">{t("لا توجد نتائج")}</p>
      ) : (
        <div className="space-y-3" data-testid="activity-table">
          {filtered.map((row, i) => <ClientRow key={row.email} row={row} index={i} t={t} />)}
        </div>
      )}
    </div>
  );
};
